import { AssetPath } from '@gnsx/genesys.js';

import { TEST_USER_OVERRIDE, TEST_USERS } from '../../const';
import { storageFactory, StorageProviderType } from '../storage';

import { AssetFilters } from './scenarioAssetFilters';
import { scenarioClient } from './scenarioClient';
import { isDuplicateTagError } from './scenarioErrors';
import { JobPoller } from './scenarioJobPoller';

import type { ProgressCallback } from './scenarioJobPoller';
import type {
  ScenarioGenerationResult,
  ScenarioImageParams,
  ScenarioModel3DParams,
  ScenarioSkyboxParams,
  ScenarioTextureConversionParams,
  ScenarioTextureParams,
} from './scenarioTypes';

const isDev = process.env.NODE_ENV === 'development';

/**
 * Debug logging controls for ScenarioService
 */
const DEBUG = {
  generation: isDev,
  tagging: isDev,
  userAssets: isDev,
  saving: isDev,
  referenceImages: isDev,
} as const;

/**
 * Get the current user ID for asset tagging
 */
export function getCurrentUserId(firebaseUid?: string): string {
  if (TEST_USER_OVERRIDE) {
    const testUser = TEST_USERS[TEST_USER_OVERRIDE];
    return testUser.uid;
  }

  if (!firebaseUid) {
    throw new Error('No user ID available - user must be authenticated');
  }

  return firebaseUid;
}

/**
 * Generate user tag for Scenario assets
 */
export function generateUserTag(userId: string): string {
  return `user:${userId}`;
}

/**
 * High-level service for Scenario AI image generation
 */
export class ScenarioService {
  private _storage?: ReturnType<typeof storageFactory.getProvider>;

  constructor(
    private client = scenarioClient,
    private poller = new JobPoller((jobId: string) => scenarioClient.getJob(jobId))
  ) { }

  private get storage() {
    if (!this._storage) {
      this._storage = storageFactory.getProvider(StorageProviderType.FILE_SERVER);
    }
    return this._storage;
  }

  /**
   * Tag assets with user ID after successful generation
   */
  private async tagUserAssets(
    assetIds: string[],
    additionalTags: string[],
    firebaseUid?: string
  ): Promise<void> {
    if (assetIds.length === 0) return;

    const userId = getCurrentUserId(firebaseUid);
    const userTag = generateUserTag(userId);
    const tags = [userTag, ...additionalTags];

    try {
      await this.client.tagAssets(assetIds, tags);
      if (DEBUG.tagging) console.log(`✅ Tagged ${assetIds.length} assets with tags:`, tags);
    } catch (error: any) {
      if (isDuplicateTagError(error)) {
        if (DEBUG.tagging) console.log('ℹ️ Assets already have the correct tags, skipping duplicate tagging');
      } else {
        console.error('Failed to tag assets:', error);
      }
      // Don't fail the generation if tagging fails
    }
  }

  /**
   * Extract job ID from various response structures
   */
  private extractJobId(response: any): string {
    const jobId =
      response.jobId ?? response.inference?.jobId ?? response.job?.jobId ?? response.id ?? response.inferenceId;

    if (!jobId || jobId === 'undefined' || jobId === 'null') {
      console.error('❌ No valid job ID found in response:', response);
      throw new Error(`No job ID returned from API. Response: ${JSON.stringify(response, null, 2)}`);
    }

    return jobId;
  }

  /**
   * Common workflow for generation operations
   */
  private async executeGenerationWorkflow(
    apiCall: () => Promise<any>,
    tags: string[],
    firebaseUid?: string,
    onProgress?: ProgressCallback,
    use3DPoller = false,
    context?: ScenarioGenerationResult['context']
  ): Promise<ScenarioGenerationResult> {
    const response = await apiCall();
    const jobId = this.extractJobId(response);

    if (DEBUG.generation) console.log('Generation started, job ID:', jobId);

    // Poll for completion using appropriate poller
    const result = use3DPoller ? await this.poller.poll3D(jobId, onProgress) : await this.poller.poll(jobId, onProgress);

    // Tag assets with user ID after successful generation
    if (result.status === 'success' && result.images.length > 0) {
      await this.tagUserAssets(result.images, tags, firebaseUid);
    }

    // Return result with context for better error messages
    return {
      ...result,
      context,
    };
  }

  /**
   * Generate skybox using text-to-skybox with progress tracking
   */
  async generateSkybox(
    params: ScenarioSkyboxParams,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    if (DEBUG.referenceImages && params.referenceImage) {
      console.log('🖼️ Generating skybox with reference image:', {
        id: params.referenceImage.id,
        mode: params.referenceImage.mode,
        influence: params.referenceImage.influence,
        source: params.referenceImage.source,
      });
    }

    return this.executeGenerationWorkflow(
      () => this.client.createSkyboxInference(params),
      ['type:skybox'],
      firebaseUid,
      onProgress,
      false,
      { generationType: 'skybox', prompt: params.prompt }
    );
  }

  /**
   * Upscale an existing skybox with progress tracking
   */
  async upscaleSkybox(
    assetId: string,
    scalingFactor: number = 2,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    return this.executeGenerationWorkflow(
      () => this.client.upscaleSkybox({ assetId, scalingFactor }),
      ['upscaled:true'],
      firebaseUid,
      onProgress,
      false,
      { generationType: 'skybox' }
    );
  }

  /**
   * Convert a texture to multiple texture maps with progress tracking
   */
  async convertTexture(
    params: ScenarioTextureConversionParams,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    return this.executeGenerationWorkflow(
      () => this.client.convertTexture(params),
      ['texture-maps:true'],
      firebaseUid,
      onProgress,
      false,
      { generationType: 'texture' }
    );
  }

  /**
   * Generate texture with progress tracking
   */
  async generateTexture(
    params: ScenarioTextureParams,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    try {
      const tags: string[] = [];
      const context: ScenarioGenerationResult['context'] = {
        generationType: 'texture',
        modelId: params.modelId,
        prompt: params.prompt,
      };

      // Check if reference image is provided and select appropriate API
      let apiCall: () => Promise<any>;

      if (params.referenceImage) {
        if (DEBUG.referenceImages) console.log(`🖼️ Using reference image with mode: ${params.referenceImage.mode}`);

        if (params.referenceImage.mode === 'img2img') {
          const img2imgParams = {
            ...params,
            image: params.referenceImage.id,
            influence: params.referenceImage.influence,
          };
          delete (img2imgParams as any).referenceImage;
          apiCall = () => this.client.generateImg2ImgTexture(img2imgParams);
          tags.push('reference:img2img');
        } else if (params.referenceImage.mode === 'controlnet') {
          const controlnetParams = {
            ...params,
            controlImage: params.referenceImage.id,
            influence: params.referenceImage.influence,
          };
          delete (controlnetParams as any).referenceImage;
          apiCall = () => this.client.generateControlNetTexture(controlnetParams);
          tags.push('reference:controlnet');
        } else {
          throw new Error(`Unsupported reference image mode: ${params.referenceImage.mode}`);
        }
      } else {
        apiCall = () => this.client.generateTexture(params);
      }

      return await this.executeGenerationWorkflow(apiCall, tags, firebaseUid, onProgress, false, context);
    } catch (error) {
      console.error('Texture generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate images using text-to-image with progress tracking
   */
  async generateImage(
    params: ScenarioImageParams,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    try {
      if (DEBUG.referenceImages && params.referenceImage) {
        console.log('🖼️ Generating image with reference image:', {
          id: params.referenceImage.id,
          mode: params.referenceImage.mode,
          influence: params.referenceImage.influence,
          source: params.referenceImage.source,
        });
      }

      const tags = ['type:image'];
      if (params.referenceImage) {
        tags.push(`reference:${params.referenceImage.mode}`);
      }

      const context: ScenarioGenerationResult['context'] = {
        generationType: 'image',
        modelId: params.modelId,
        modelName: params.modelName,
        prompt: params.prompt,
      };

      return await this.executeGenerationWorkflow(() => this.client.generateImage(params), tags, firebaseUid, onProgress, false, context);
    } catch (error) {
      console.error('Image generation failed:', error);
      throw error;
    }
  }

  /**
   * Remove background from an image with progress tracking
   */
  async removeBackground(
    assetId: string,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    try {
      return await this.executeGenerationWorkflow(
        () => this.client.removeBackground(assetId),
        ['background-removed:true'],
        firebaseUid,
        onProgress,
        false,
        { generationType: 'image' }
      );
    } catch (error) {
      console.error('Background removal failed:', error);
      throw error;
    }
  }

  /**
   * Generate 3D model from image with progress tracking
   */
  async generateModel3D(
    params: ScenarioModel3DParams,
    onProgress?: ProgressCallback,
    firebaseUid?: string
  ): Promise<ScenarioGenerationResult> {
    try {
      return await this.executeGenerationWorkflow(
        () => this.client.generateModel3D(params),
        ['type:3d'],
        firebaseUid,
        onProgress,
        true, // Use 3D poller
        { generationType: '3d', modelId: params.modelId, prompt: params.prompt }
      );
    } catch (error) {
      console.error('3D generation failed:', error);
      throw error;
    }
  }

  /** @deprecated Use generateModel3D instead */
  async generate3D(params: ScenarioModel3DParams, onProgress?: ProgressCallback, firebaseUid?: string): Promise<ScenarioGenerationResult> {
    return this.generateModel3D(params, onProgress, firebaseUid);
  }

  /**
   * Download and save generated images to project storage
   */
  async saveGeneratedImages(assetIds: string[], projectPath: string, prompt: string): Promise<string[]> {
    const savedPaths: string[] = [];

    // Normalize path - remove @project/ prefix if present since AssetPath.fromString adds it
    const normalizedPath = projectPath.startsWith('@project/')
      ? projectPath.substring('@project/'.length)
      : projectPath;

    for (let i = 0; i < assetIds.length; i++) {
      const assetId = assetIds[i];

      try {
        // Get asset metadata to determine the correct file type
        const assetInfo = await this.client.getAsset(assetId);

        // Download asset from Scenario
        const assetBlob = await this.client.downloadAsset(assetId);

        // Determine file extension based on asset metadata or MIME type
        let fileExtension = '.png'; // Default fallback

        if (assetInfo.metadata?.format) {
          const format = assetInfo.metadata.format.toLowerCase();
          if (format === 'glb') fileExtension = '.glb';
          else if (format === 'jpg' || format === 'jpeg') fileExtension = '.jpg';
          else if (format === 'png') fileExtension = '.png';
          else if (format === 'mp4') fileExtension = '.mp4';
          else if (format === 'webp') fileExtension = '.webp';
        } else if (assetBlob.type) {
          if (assetBlob.type === 'model/gltf-binary') fileExtension = '.glb';
          else if (assetBlob.type.startsWith('image/jpeg')) fileExtension = '.jpg';
          else if (assetBlob.type.startsWith('image/png')) fileExtension = '.png';
          else if (assetBlob.type.startsWith('video/mp4')) fileExtension = '.mp4';
          else if (assetBlob.type.startsWith('image/webp')) fileExtension = '.webp';
        }

        // Generate descriptive filename based on asset type and metadata
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').substring(0, 19);

        // Extract prompt from asset metadata or inference parameters
        let assetPrompt = (assetInfo as any).parameters?.prompt || assetInfo.metadata?.prompt || prompt || '';

        // Clean and truncate prompt for filename (max 40 chars, remove special chars)
        const promptSlug = assetPrompt
          .trim()
          .slice(0, 40)
          .replace(/[^a-zA-Z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .toLowerCase();

        // Determine asset category and subtype
        const { category, subtype } = AssetFilters.getAssetCategory(assetInfo);

        // Build filename with prompt prefix if available
        const promptPrefix = promptSlug ? `${promptSlug}_` : '';
        const filename = `${promptPrefix}${category}${subtype}_${timestamp}${fileExtension}`;
        const assetPath = `${normalizedPath}/${filename}`;

        // Save to storage
        await this.storage.uploadFile(AssetPath.fromString(assetPath), assetBlob);
        savedPaths.push(assetPath);

        if (DEBUG.saving) {
          console.log(
            `✅ Saved ${category}${subtype} (${assetInfo.metadata?.format || assetBlob.type || 'unknown'}): ${assetPath}`
          );
        }
      } catch (error) {
        console.error(`Failed to save asset ${assetId}:`, error);
      }
    }

    return savedPaths;
  }

  /**
   * Save assets to project - uses the same logic as saveGeneratedImages
   */
  async saveAssetsToProject(
    assetIds: string[],
    assetType: 'skybox' | 'texture' | 'image' | '3d' = 'skybox',
    customPath?: string
  ): Promise<void> {
    // Use custom path if provided, otherwise use default organized paths
    const projectPath =
      customPath ||
      (assetType === 'skybox'
        ? 'assets/generated/skybox'
        : assetType === 'texture'
          ? 'assets/generated/textures'
          : assetType === '3d'
            ? 'assets/generated/mesh'
            : 'assets/generated/images');

    const prompt = 'saved_asset'; // Generic prompt since we don't have the original

    await this.saveGeneratedImages(assetIds, projectPath, prompt);
  }

  /**
   * Fetch assets for the current user
   */
  async fetchUserAssets(firebaseUid?: string, assetType?: 'skybox' | 'texture' | 'image' | '3d'): Promise<any> {
    try {
      const userId = getCurrentUserId(firebaseUid);
      const userTag = generateUserTag(userId);

      // Fetch all assets first (since tag filtering requires public privacy)
      let allAssets;
      if (assetType === '3d') {
        allAssets = await this.client.listAssets({});
      } else {
        const filter = assetType === 'skybox' ? 'skybox' : assetType === 'image' ? 'image' : 'texture';
        allAssets = await this.client.listAssets({ filter });
      }

      if (DEBUG.userAssets) console.log(`📦 Fetched ${allAssets.assets?.length ?? 0} total ${assetType} assets`);

      // Filter by type using AssetFilters
      const typeFilteredAssets = AssetFilters.filterByType(allAssets.assets ?? [], assetType);

      // Filter by user tag
      const { userAssets, legacyAssets, otherUserAssets } = AssetFilters.filterByUser(typeFilteredAssets, userTag);

      // Return only user-tagged assets (exclude legacy untagged assets to avoid showing fallback assets)
      const assetsToReturn = userAssets;

      if (DEBUG.userAssets) {
        console.log(
          `👤 Asset summary - User: ${userAssets.length}, Legacy: ${legacyAssets.length}, Others: ${otherUserAssets.length}, Returning: ${assetsToReturn.length}`
        );
      }

      return {
        ...allAssets,
        assets: assetsToReturn,
      };
    } catch (error) {
      console.error('Failed to fetch user assets:', error);
      throw error;
    }
  }

  /**
   * Upload an image file and return asset ID for use as reference image
   */
  async uploadReferenceImage(imageFile: File | Blob, name?: string): Promise<string> {
    try {
      const assetId = await this.client.uploadImage(imageFile, name);

      // Optionally tag the uploaded image for easier filtering
      try {
        await this.client.tagAssets([assetId], ['type:reference-image']);
        if (DEBUG.referenceImages) console.log('✅ Tagged reference image');
      } catch (error: any) {
        if (isDuplicateTagError(error)) {
          if (DEBUG.referenceImages)
            console.log('ℹ️ Reference image already has the correct tag, skipping tagging');
        } else {
          console.warn('Failed to tag uploaded reference image:', error);
        }
        // Don't fail the upload if tagging fails
      }

      return assetId;
    } catch (error) {
      console.error('Failed to upload reference image:', error);
      throw error;
    }
  }

  /**
   * ONE-TIME UTILITY: Tag all existing assets with current user ID
   * This is for migrating legacy assets created before the tagging system
   */
  async tagAllExistingAssets(firebaseUid?: string, dryRun: boolean = true): Promise<void> {
    console.log('🏷️ Starting asset tagging migration...');

    const userId = getCurrentUserId(firebaseUid);
    const userTag = generateUserTag(userId);

    console.log(`👤 Current user: ${userId}`);
    console.log(`🏷️ User tag: ${userTag}`);
    console.log(
      `🧪 Dry run mode: ${dryRun ? 'ON (no changes will be made)' : 'OFF (will actually tag assets)'}`
    );

    try {
      // Get all skybox assets
      console.log('📦 Fetching all skybox assets...');
      const skyboxAssets = await this.client.listAssets({ filter: 'skybox' });
      console.log(`Found ${skyboxAssets.assets?.length ?? 0} skybox assets`);

      // Get all texture assets
      console.log('📦 Fetching all texture assets...');
      const textureAssets = await this.client.listAssets({ filter: 'texture' });
      console.log(`Found ${textureAssets.assets?.length ?? 0} texture assets`);

      // Create a combined list with type information
      const allAssetsWithType = [
        ...(skyboxAssets.assets ?? []).map(asset => ({ ...asset, assetType: 'skybox' as const })),
        ...(textureAssets.assets ?? []).map(asset => ({ ...asset, assetType: 'texture' as const })),
      ];

      console.log(`📊 Total assets to process: ${allAssetsWithType.length}`);

      let taggedCount = 0;
      let alreadyTaggedCount = 0;
      let errorCount = 0;

      for (let i = 0; i < allAssetsWithType.length; i++) {
        const { assetType, ...asset } = allAssetsWithType[i];
        const assetTags = asset.tags ?? [];

        // Check if asset already has a user tag
        const hasUserTag = assetTags.some((tag: string) => tag.startsWith('user:'));

        if (hasUserTag) {
          alreadyTaggedCount++;
          continue;
        }

        const tagsToAdd = [userTag, `type:${assetType}`];

        console.log(`🏷️ Asset ${i + 1}/${allAssetsWithType.length}: ${asset.id} (${assetType})`);
        console.log(`   Current tags: [${assetTags.join(', ')}]`);
        console.log(`   Will add: [${tagsToAdd.join(', ')}]`);

        if (!dryRun) {
          try {
            await this.client.tagAssets([asset.id], tagsToAdd);
            taggedCount++;

            // Add small delay to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error: any) {
            if (isDuplicateTagError(error)) {
              console.log(`   ℹ️ Asset ${asset.id} already has correct tags, skipping duplicate tagging`);
              alreadyTaggedCount++;
            } else {
              console.error(`   ❌ Failed to tag asset ${asset.id}:`, error);
              errorCount++;
            }
          }
        } else {
          console.log(`   🧪 DRY RUN - would tag with: [${tagsToAdd.join(', ')}]`);
          taggedCount++;
        }
      }

      console.log('\n📊 Migration Summary:');
      console.log(`   📦 Total assets processed: ${allAssetsWithType.length}`);
      console.log(`   ✅ Assets tagged: ${taggedCount}`);
      console.log(`   🏷️ Already had user tags: ${alreadyTaggedCount}`);
      console.log(`   ❌ Errors: ${errorCount}`);

      if (dryRun) {
        console.log('\n🧪 This was a DRY RUN - no changes were made.');
        console.log('💡 To actually apply the tags, call with dryRun: false');
      } else {
        console.log('\n✅ Migration completed!');
      }
    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }
}

/** Export singleton instance */
export const scenarioService = new ScenarioService();
